import { createContext, useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import getMonth from 'date-fns/getMonth'
import getYear from 'date-fns/getYear'
import lastDayOfMonth from 'date-fns/lastDayOfMonth'
import eachWeekOfInterval from 'date-fns/eachWeekOfInterval'
import eachDayOfInterval from 'date-fns/eachDayOfInterval'
import isWithinInterval from 'date-fns/isWithinInterval'
import addDays from 'date-fns/addDays'
import addMonths from 'date-fns/addMonths'
import format from 'date-fns/format'
import isSameDay from 'date-fns/isSameDay'
import isBefore from 'date-fns/isBefore'
import { isSameMonth, min, max, differenceInYears } from 'date-fns'
import { Overlay } from '../overlay'

import { CalendarInputArrow } from './calendarInputArrow'
import styles from './calendarInput.module.scss'

const StateContext = createContext()

const Day = ({ day }) => {
  const {
    type,
    startDay,
    setStartDay,
    endDay,
    setEndDay,
    hoverDay,
    setHoverDay,
    mode,
  } = useContext(StateContext)

  const isOneWay = type === 'OW'
  const isCurrentDate = isSameDay(new Date(), day)
  const isActiveDate = isSameDay(startDay, day) || (isSameDay(endDay, day) && !isOneWay)
  const disabled = isBefore(day, new Date()) && !isCurrentDate

  let isInRange = false
  if (startDay && (endDay || hoverDay)) {
    const rangeEnd = endDay || hoverDay
    const start = min([startDay, rangeEnd])
    const end = max([startDay, rangeEnd])

    isInRange = isWithinInterval(day, { start, end }) && !isOneWay
  }

  const handleSelect = () => {
    if (isOneWay || !startDay || mode === 'start') {
      if (!endDay) {
        setStartDay(day)
        setEndDay(addDays(day, 7))
      } else {
        setStartDay(min([endDay, day]))
        setEndDay(max([endDay, day]))
      }
    } else if (startDay && mode === 'end') {
      setStartDay(min([startDay, day]))
      setEndDay(max([startDay, day]))
    }
  }

  const handleHover = () => {
    if (startDay && !isOneWay) {
      setHoverDay(day)
    }
  }

  const classNames = [styles.calendarInput__date]

  if (isCurrentDate) {
    classNames.push(styles.calendarInput__date_current)
  }
  if (isInRange) {
    classNames.push(styles.calendarInput__date_range)
  }
  if (isActiveDate) {
    classNames.push(styles.calendarInput__date_start)
  }

  return (
    <td>
      <button
        type="button"
        className={classNames.join(' ')}
        disabled={disabled}
        onClick={handleSelect}
        onMouseEnter={handleHover}
      >
        {format(day, 'd')}
      </button>
    </td>
  )
}
Day.propTypes = {
  day: PropTypes.object,
}

const Week = ({ week, removeMonthDiff }) => {
  const { currentMonth, secondMonth } = useContext(StateContext)

  return (
    <tr>
      {week.map((day, index) => {
        if (removeMonthDiff === 'next') {
          if (isSameMonth(day, secondMonth)) {
            return <td key={index} />
          }
        }

        if (removeMonthDiff === 'prev') {
          if (isSameMonth(day, currentMonth)) {
            return <td key={index} />
          }
        }

        return <Day day={day} key={index} />
      })}
    </tr>
  )
}
Week.propTypes = {
  week: PropTypes.array,
  removeMonthDiff: PropTypes.oneOf(['next', 'prev']),
}

const Table = ({ weeks, removeMonthDiff, ...rest }) => {
  return (
    <table {...rest}>
      <thead>
        <tr className={styles.weekDays}>
          <td>S</td>
          <td>M</td>
          <td>T</td>
          <td>W</td>
          <td>T</td>
          <td>F</td>
          <td>S</td>
        </tr>
      </thead>
      <tbody>
        {weeks.map((start, index) => {
          const weekInterval = { start, end: addDays(start, 6) }
          const week = eachDayOfInterval(weekInterval)
          return (
            <Week removeMonthDiff={removeMonthDiff} key={index} weekIndex={index} week={week} />
          )
        })}
      </tbody>
    </table>
  )
}
Table.propTypes = {
  weeks: PropTypes.array,
  removeMonthDiff: PropTypes.oneOf(['next', 'prev']),
}

const getWeeks = (startOfTheMonth) => {
  const endOfTheMonth = lastDayOfMonth(startOfTheMonth)

  const monthInterval = { start: startOfTheMonth, end: endOfTheMonth }
  const weeks = eachWeekOfInterval(monthInterval)

  return weeks
}

export const CalendarInput = ({ mode, type, setDays, open, toggleOpen, overlayRefs }) => {
  const currentTime = new Date()
  const [currentMonth, setCurrentMonth] = useState(
    new Date(getYear(currentTime), getMonth(currentTime))
  )
  const secondMonth = addMonths(currentMonth, 1)

  const [startDay, setStartDay] = useState(null)
  const [hoverDay, setHoverDay] = useState(null)
  const [endDay, setEndDay] = useState(null)

  const shiftMonth = (offset) => {
    setCurrentMonth(addMonths(currentMonth, offset))
  }

  useEffect(() => {
    setDays({ startDay, endDay })
  }, [startDay, endDay])

  const nextButton = (
    <button
      type="button"
      className={styles.nextButton}
      disabled={differenceInYears(currentTime, addMonths(currentMonth, 1)) < 0}
      onClick={() => {
        if (differenceInYears(currentTime, addMonths(currentMonth, 1)) < 0) {
          return
        }

        shiftMonth(1)
      }}
    >
      <CalendarInputArrow className={styles.arrow} />
    </button>
  )

  return (
    <StateContext.Provider
      value={{
        type,
        startDay,
        setStartDay,
        endDay,
        setEndDay,
        hoverDay,
        setHoverDay,
        currentMonth,
        secondMonth,
        mode,
      }}
    >
      <Overlay
        includeRefs={overlayRefs}
        isOpen={open}
        openChange={toggleOpen}
        className={`${styles.overlay} ${type === 'OW' ? styles.oneWay : ''}`}
      >
        <div className={styles.calendarInput}>
          <div className={styles.tablesContainer}>
            <div>
              <button
                type="button"
                className={styles.prevButton}
                onClick={(e) => {
                  e.preventDefault()

                  shiftMonth(-1)
                }}
              >
                <CalendarInputArrow className={styles.arrow} />
              </button>
              {format(currentMonth, 'MMMM yyyy')}
              {nextButton}
              <Table
                removeMonthDiff="next"
                weeks={getWeeks(currentMonth)}
                className={styles.calendarInput__table}
              />
            </div>
            <div>
              {format(secondMonth, 'MMMM yyyy')}
              {nextButton}
              <Table
                removeMonthDiff="prev"
                weeks={getWeeks(secondMonth)}
                className={styles.calendarInput__table}
              />
            </div>
          </div>
        </div>
      </Overlay>
    </StateContext.Provider>
  )
}

CalendarInput.propTypes = {
  type: PropTypes.oneOf(['OW', 'RT']).isRequired,
  mode: PropTypes.oneOf(['start', 'end']),
  setDays: PropTypes.func,
  open: PropTypes.bool,
  toggleOpen: PropTypes.func,
  overlayRefs: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.any,
      PropTypes.func,
      PropTypes.shape({ current: PropTypes.elementType }),
    ])
  ),
}
